import React, { useState, useRef, useMemo, useCallback } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
const QuillEditor: any = ReactQuill;
import 'react-quill-new/dist/quill.snow.css';
import { marked } from 'marked';
import {
    Eye, Code, Save, Layout, Calculator, FileSignature,
    Type, Hash, Table as TableIcon, Search, HelpCircle,
    Image as ImageIcon, Link as LinkIcon, UploadCloud, X
} from 'lucide-react';
import { FORM_VARIABLE_GROUPS as FORM_VARIABLES } from '../../lib/templateEngine';

const Size: any = Quill.import('attributors/style/size');
Size.whitelist = ['8px', '9px', '10px', '11px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px'];
(Quill as any).register(Size, true);

const Font: any = Quill.import('attributors/class/font');
Font.whitelist = ['serif', 'monospace', 'arial', 'times-new-roman', 'georgia', 'verdana', 'courier-new', 'trebuchet-ms'];
(Quill as any).register(Font, true);

marked.setOptions({ breaks: true, gfm: true });

const SECTION_TABS = [
    { id: 'header', label: 'Header', icon: Layout },
    { id: 'body',   label: 'Body',   icon: FileSignature },
    { id: 'footer', label: 'Footer', icon: Calculator },
];

interface FormTemplateEditorProps {
    template?: any;
    user?: any;
    onSave: (templateData: any) => void;
    onCancel: () => void;
    showToast?: (msg: string) => void;
}

const FormTemplateEditor: React.FC<FormTemplateEditorProps> = ({ template, onSave, onCancel, showToast }) => {
    const [activeTemplate, setActiveTemplate] = useState(() => {
        const init = template || { name: '', type: 'HTML', templateCategory: 'form' };
        return {
            ...init,
            sections: template?.sections || {
                header: { content: template?.content || '', mode: 'rich' },
                body: { content: '', mode: 'rich' },
                footer: { content: '', mode: 'rich' }
            }
        };
    });

    const [activeTab, setActiveTab] = useState('body');
    const [editorMode, setEditorMode] = useState('rich'); // 'rich' | 'markdown' | 'html'
    const [viewMode, setViewMode] = useState('edit'); // 'edit' | 'preview'
    const [searchQuery, setSearchQuery] = useState('');

    // Image Insertion Modal States
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [imageUrlInput, setImageUrlInput] = useState('');
    const [imageAltInput, setImageAltInput] = useState('');

    const quillRef = useRef(null);
    const textareaRef = useRef(null);

    const currentSection = activeTemplate.sections[activeTab] || { content: '', mode: 'rich' };

    const updateSectionContent = (content) => {
        setActiveTemplate(prev => ({
            ...prev,
            sections: {
                ...prev.sections,
                [activeTab]: {
                    ...prev.sections[activeTab],
                    content,
                    mode: editorMode
                }
            }
        }));
    };

    // Callback when Image Toolbar Button in Quill is clicked
    const imageHandler = useCallback(() => {
        setIsImageModalOpen(true);
    }, []);

    // Insert Image into active editor selection across Rich Text, Markdown, or HTML Source modes
    const handleImageInsert = useCallback((url, altText = '') => {
        if (!url || !url.trim()) return;
        const cleanUrl = url.trim();

        if (editorMode === 'rich' && quillRef.current) {
            const editor = quillRef.current.getEditor();
            const range = editor.getSelection(true);
            editor.insertEmbed(range.index, 'image', cleanUrl);
            editor.setSelection(range.index + 1);
        } else if (textareaRef.current) {
            const textarea = textareaRef.current;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const val = textarea.value || '';
            const imgCode = editorMode === 'markdown'
                ? `\n![${altText || 'Image'}](${cleanUrl})\n`
                : `\n<img src="${cleanUrl}" alt="${altText || 'Image'}" style="max-width:100%; height:auto;" />\n`;
            const newVal = val.substring(0, start) + imgCode + val.substring(end);
            updateSectionContent(newVal);
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start + imgCode.length, start + imgCode.length);
            }, 0);
        } else {
            const imgCode = editorMode === 'markdown'
                ? `\n![${altText || 'Image'}](${cleanUrl})\n`
                : `\n<img src="${cleanUrl}" alt="${altText || 'Image'}" style="max-width:100%; height:auto;" />\n`;
            updateSectionContent((currentSection.content || '') + imgCode);
        }
    }, [editorMode, currentSection.content]);

    // Quill Toolbar Modules matching exact requested toolbar layout
    const modules = useMemo(() => ({
        toolbar: {
            container: [
                [{ 'font': Font.whitelist }],
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                [{ 'size': Size.whitelist }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'script': 'sub' }, { 'script': 'super' }],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                [{ 'indent': '-1' }, { 'indent': '+1' }],
                [{ 'align': [] }],
                ['blockquote', 'code-block'],
                ['link', 'image'],
                ['clean']
            ],
            handlers: {
                image: imageHandler
            }
        }
    }), [imageHandler]);

    const formats = [
        'font', 'header', 'size',
        'bold', 'italic', 'underline', 'strike',
        'script',
        'color', 'background',
        'list', 'bullet', 'indent',
        'align',
        'blockquote', 'code-block',
        'link', 'image'
    ];

    const insertVariable = (varName) => {
        const tag = `{{${varName}}}`;
        if (editorMode === 'rich' && quillRef.current) {
            const editor = quillRef.current.getEditor();
            const range = editor.getSelection(true);
            editor.insertText(range.index, tag);
            editor.setSelection(range.index + tag.length);
        } else if (textareaRef.current) {
            const textarea = textareaRef.current;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const val = textarea.value || '';
            const newVal = val.substring(0, start) + tag + val.substring(end);
            updateSectionContent(newVal);
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start + tag.length, start + tag.length);
            }, 0);
        } else {
            updateSectionContent((currentSection.content || '') + tag);
        }
    };

    const insertTable = () => {
        if (editorMode === 'rich' && quillRef.current) {
            const editor = quillRef.current.getEditor();
            const range = editor.getSelection(true);
            const tableHtml = `
<table style="border-collapse: collapse; width: 100%; margin: 12px 0; border: 1px solid #cbd5e1;">
  <thead>
    <tr style="background-color: #f8fafc;">
      <th style="padding: 8px 12px; border: 1px solid #cbd5e1; text-align: left;">Field</th>
      <th style="padding: 8px 12px; border: 1px solid #cbd5e1; text-align: left;">Details</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td style="padding: 8px 12px; border: 1px solid #cbd5e1;">Service Type</td>
      <td style="padding: 8px 12px; border: 1px solid #cbd5e1;">{{ServiceType}}</td>
    </tr>
    <tr>
      <td style="padding: 8px 12px; border: 1px solid #cbd5e1;">Rate Quote</td>
      <td style="padding: 8px 12px; border: 1px solid #cbd5e1;">{{QuoteDetails}}</td>
    </tr>
  </tbody>
</table>
`;
            editor.clipboard.dangerouslyPasteHTML(range.index, tableHtml);
        } else if (editorMode === 'markdown') {
            const tableMd = `\n| Field | Details |\n| --- | --- |\n| Service Type | {{ServiceType}} |\n| Rate Quote | {{QuoteDetails}} |\n\n`;
            const val = currentSection.content || '';
            updateSectionContent(val + tableMd);
        } else {
            const tableHtml = `\n<table border="1" style="border-collapse: collapse; width: 100%; margin: 10px 0;">\n  <tr><th style="padding:6px;">Field</th><th style="padding:6px;">Details</th></tr>\n  <tr><td style="padding:6px;">Service Type</td><td style="padding:6px;">{{ServiceType}}</td></tr>\n</table>\n`;
            const val = currentSection.content || '';
            updateSectionContent(val + tableHtml);
        }
    };

    const renderMarkdownOrHtml = (content) => {
        if (!content) return '';
        if (editorMode === 'markdown') {
            try {
                return marked.parse(content);
            } catch {
                return content;
            }
        }
        return content;
    };

    const renderedPreviewHtml = useMemo(() => {
        const header = renderMarkdownOrHtml(activeTemplate.sections?.header?.content || '');
        const body = renderMarkdownOrHtml(activeTemplate.sections?.body?.content || '');
        const footer = renderMarkdownOrHtml(activeTemplate.sections?.footer?.content || '');

        return `
            <div style="font-family: system-ui, -apple-system, sans-serif; padding: 24px; color: #1e293b; line-height: 1.6;">
                ${header ? `<header style="margin-bottom: 24px;">${header}</header>` : ''}
                <main style="margin-bottom: 24px;">${body}</main>
                ${footer ? `<footer style="border-top: 1px solid #e2e8f0; padding-top: 16px; margin-top: 24px; font-size: 0.9em; color: #64748b;">${footer}</footer>` : ''}
            </div>
        `;
    }, [activeTemplate, editorMode]);

    const filteredVariables = useMemo(() => {
        if (!searchQuery.trim()) return FORM_VARIABLES;
        const q = searchQuery.toLowerCase();
        return FORM_VARIABLES.map(group => ({
            ...group,
            vars: group.vars.filter(v => v.name.toLowerCase().includes(q) || v.label.toLowerCase().includes(q))
        })).filter(group => group.vars.length > 0);
    }, [searchQuery]);

    return (
        <div className="max-w-6xl mx-auto h-full flex flex-col bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-lg">
            {/* Top Bar */}
            <div className="bg-white dark:bg-slate-800 px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <input
                        type="text"
                        placeholder="Form Template Name..."
                        value={activeTemplate.name}
                        onChange={e => setActiveTemplate(prev => ({ ...prev, name: e.target.value }))}
                        className="text-lg font-bold bg-transparent border-b border-dashed border-slate-300 dark:border-slate-600 focus:border-indigo-500 focus:outline-none text-slate-800 dark:text-slate-100 min-w-[280px]"
                    />
                </div>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setViewMode(viewMode === 'edit' ? 'preview' : 'edit')}
                        className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 flex items-center gap-1.5 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                        {viewMode === 'edit' ? <Eye size={14} /> : <Code size={14} />}
                        {viewMode === 'edit' ? 'Preview' : 'Editor'}
                    </button>
                    <button
                        type="button"
                        onClick={() => onSave(activeTemplate)}
                        className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-xs rounded-md shadow flex items-center gap-1.5 cursor-pointer"
                    >
                        <Save size={14} /> Save Template
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md cursor-pointer"
                    >
                        Cancel
                    </button>
                </div>
            </div>

            {/* Section Tabs & Mode Selector Bar */}
            <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 px-4">
                {/* Section Selector */}
                <div className="flex">
                    {SECTION_TABS.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-5 py-3 text-xs font-bold border-b-2 transition-colors cursor-pointer ${
                                    activeTab === tab.id ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400' : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                                }`}
                            >
                                <Icon size={14} /> {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* Format Toggles & Insert Tools */}
                <div className="flex items-center gap-2 py-2">
                    <div className="flex items-center bg-slate-100 dark:bg-slate-900 p-1 rounded-lg border border-slate-200 dark:border-slate-800">
                        <button
                            type="button"
                            onClick={() => setEditorMode('rich')}
                            className={`px-3 py-1 text-xs font-bold rounded-md flex items-center gap-1 transition cursor-pointer ${
                                editorMode === 'rich'
                                    ? 'bg-indigo-600 text-white shadow-sm'
                                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                            }`}
                        >
                            <Type size={13} /> Rich Text
                        </button>
                        <button
                            type="button"
                            onClick={() => setEditorMode('markdown')}
                            className={`px-3 py-1 text-xs font-bold rounded-md flex items-center gap-1 transition cursor-pointer ${
                                editorMode === 'markdown'
                                    ? 'bg-indigo-600 text-white shadow-sm'
                                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                            }`}
                        >
                            <Hash size={13} /> Markdown
                        </button>
                        <button
                            type="button"
                            onClick={() => setEditorMode('html')}
                            className={`px-3 py-1 text-xs font-bold rounded-md flex items-center gap-1 transition cursor-pointer ${
                                editorMode === 'html'
                                    ? 'bg-indigo-600 text-white shadow-sm'
                                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                            }`}
                        >
                            <Code size={13} /> HTML Source
                        </button>
                    </div>

                    <span className="text-slate-300 dark:text-slate-700 font-light">|</span>

                    <button
                        type="button"
                        onClick={insertTable}
                        className="px-3 py-1 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-semibold text-xs rounded-md border border-slate-200 dark:border-slate-700 flex items-center gap-1.5 transition cursor-pointer"
                        title="Insert a formatted data table"
                    >
                        <TableIcon size={13} /> Table
                    </button>

                    <button
                        type="button"
                        onClick={() => setIsImageModalOpen(true)}
                        className="px-3 py-1 bg-indigo-50 dark:bg-indigo-950 hover:bg-indigo-100 dark:hover:bg-indigo-900 text-indigo-700 dark:text-indigo-300 font-semibold text-xs rounded-md border border-indigo-200 dark:border-indigo-800 flex items-center gap-1.5 transition cursor-pointer"
                        title="Insert Image from Web URL or File"
                    >
                        <ImageIcon size={13} /> Insert Image
                    </button>
                </div>
            </div>

            {/* Main Workspace */}
            <div className="flex-1 flex overflow-hidden">
                {viewMode === 'edit' ? (
                    <div className="flex-1 flex flex-col bg-white dark:bg-slate-800 overflow-hidden">
                        {editorMode === 'rich' ? (
                            <QuillEditor
                                ref={quillRef}
                                theme="snow"
                                value={currentSection.content || ''}
                                onChange={updateSectionContent}
                                modules={modules}
                                formats={formats}
                                className="flex-1 flex flex-col"
                            />
                        ) : editorMode === 'markdown' ? (
                            <div className="flex-1 flex flex-col p-4 bg-slate-950 text-slate-100 font-mono text-xs">
                                <div className="text-[10px] text-slate-400 mb-2 flex items-center gap-1">
                                    <Hash size={12} /> Editing in Markdown mode. Use standard markdown syntax (# Heading, **bold**, - list).
                                </div>
                                <textarea
                                    ref={textareaRef}
                                    value={currentSection.content || ''}
                                    onChange={e => updateSectionContent(e.target.value)}
                                    placeholder="# Type your markdown content here..."
                                    className="flex-1 w-full p-4 bg-slate-900 text-slate-100 border border-slate-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 font-mono leading-relaxed resize-none"
                                />
                            </div>
                        ) : (
                            <div className="flex-1 flex flex-col p-4 bg-slate-950 text-emerald-400 font-mono text-xs">
                                <div className="text-[10px] text-slate-400 mb-2 flex items-center gap-1">
                                    <Code size={12} /> Editing raw HTML source code. Inline CSS and custom HTML tags are supported.
                                </div>
                                <textarea
                                    ref={textareaRef}
                                    value={currentSection.content || ''}
                                    onChange={e => updateSectionContent(e.target.value)}
                                    placeholder="<p>Type your HTML content here...</p>"
                                    className="flex-1 w-full p-4 bg-slate-900 text-emerald-300 border border-slate-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 font-mono leading-relaxed resize-none"
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex-1 p-8 bg-slate-100 dark:bg-slate-950 overflow-y-auto">
                        <div className="max-w-3xl mx-auto bg-white dark:bg-slate-900 p-8 rounded-xl shadow-md border border-slate-200 dark:border-slate-800 min-h-[500px]">
                            <div
                                className="prose dark:prose-invert max-w-none"
                                dangerouslySetInnerHTML={{ __html: renderedPreviewHtml }}
                            />
                        </div>
                    </div>
                )}

                {/* Variable Sidebar */}
                <div className="w-72 bg-slate-50 dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 p-4 flex flex-col overflow-hidden shrink-0">
                    <div className="mb-3">
                        <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2 flex items-center justify-between">
                            <span>CRM Lead Variables</span>
                            <HelpCircle size={12} className="text-slate-400" title="Click any variable to insert it into your active template position" />
                        </h4>
                        <div className="relative">
                            <Search size={12} className="absolute left-2.5 top-2.5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search Lead fields..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="w-full pl-7 pr-3 py-1.5 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md focus:outline-none focus:border-indigo-500 text-slate-800 dark:text-slate-200"
                            />
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto space-y-4 pr-1">
                        {filteredVariables.map(group => (
                            <div key={group.group} className="bg-white dark:bg-slate-800/60 p-2.5 rounded-lg border border-slate-200/80 dark:border-slate-700/60 shadow-xs">
                                <span className="text-[10px] font-extrabold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider block mb-1.5">
                                    {group.group}
                                </span>
                                <div className="space-y-1">
                                    {group.vars.map(v => (
                                        <button
                                            key={v.name}
                                            type="button"
                                            onClick={() => insertVariable(v.name)}
                                            className="w-full text-left px-2 py-1.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 hover:text-indigo-600 dark:hover:text-indigo-300 rounded-md cursor-pointer transition flex items-center justify-between group"
                                            title={`Insert {{${v.name}}}`}
                                        >
                                            <span className="font-mono text-[11px] font-semibold text-slate-800 dark:text-slate-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                                                {`{{${v.name}}}`}
                                            </span>
                                            <span className="text-[9px] text-slate-400 dark:text-slate-500 font-sans truncate max-w-[90px]">
                                                {v.label}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Image URL / Upload Modal */}
            {isImageModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-md w-full border border-slate-200 dark:border-slate-700 overflow-hidden animate-in fade-in zoom-in duration-150">
                        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-800/80">
                            <h3 className="font-bold text-sm text-slate-800 dark:text-slate-100 flex items-center gap-2">
                                <ImageIcon size={16} className="text-indigo-600 dark:text-indigo-400" /> Insert Image from URL
                            </h3>
                            <button
                                type="button"
                                onClick={() => setIsImageModalOpen(false)}
                                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-md cursor-pointer"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        <div className="p-5 space-y-4">
                            <div>
                                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-1.5">
                                    Image Web URL (HTTP / HTTPS)
                                </label>
                                <div className="relative">
                                    <LinkIcon size={14} className="absolute left-3 top-2.5 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="https://example.com/images/logo.png"
                                        value={imageUrlInput}
                                        onChange={(e) => setImageUrlInput(e.target.value)}
                                        className="w-full pl-8 pr-3 py-2 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none text-slate-800 dark:text-slate-100 font-mono"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-1.5">
                                    Alt Text / Description (Optional)
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g. Armellini Logistics Header Logo"
                                    value={imageAltInput}
                                    onChange={(e) => setImageAltInput(e.target.value)}
                                    className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none text-slate-800 dark:text-slate-100"
                                />
                            </div>

                            {/* Local File Upload Option */}
                            <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
                                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-1.5 flex items-center gap-1">
                                    <UploadCloud size={13} /> Or Upload Image File
                                </label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            const reader = new FileReader();
                                            reader.onload = (evt) => {
                                                if (evt.target?.result) {
                                                    setImageUrlInput(evt.target.result.toString());
                                                }
                                            };
                                            reader.readAsDataURL(file);
                                        }
                                    }}
                                    className="text-xs text-slate-600 dark:text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 dark:file:bg-indigo-950 dark:file:text-indigo-300 cursor-pointer"
                                />
                            </div>

                            {/* Live Preview */}
                            {imageUrlInput && (
                                <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-900/50 flex flex-col items-center">
                                    <span className="text-[10px] uppercase font-bold text-slate-400 mb-1">Live Image Preview</span>
                                    <img
                                        src={imageUrlInput}
                                        alt={imageAltInput || 'Preview'}
                                        className="max-h-36 max-w-full object-contain rounded border border-slate-200 dark:border-slate-700"
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="px-5 py-3 bg-slate-50 dark:bg-slate-800/80 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setIsImageModalOpen(false)}
                                className="px-3.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md cursor-pointer"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={!imageUrlInput.trim()}
                                onClick={() => {
                                    handleImageInsert(imageUrlInput, imageAltInput);
                                    setImageUrlInput('');
                                    setImageAltInput('');
                                    setIsImageModalOpen(false);
                                    if (showToast) showToast('Image inserted into template editor');
                                }}
                                className="px-4 py-1.5 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white rounded-md shadow disabled:opacity-50 cursor-pointer"
                            >
                                Insert Image
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FormTemplateEditor;
